import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAccountsService } from '../services/adminAccountsService';
import {
  BarChart3,
  Users,
  DollarSign,
  TrendingUp,
  Calendar,
  Download,
  RefreshCw,
  Eye,
  Edit,
  MoreVertical,
  Activity,
  Clock,
  MapPin,
  Star,
  ArrowUpRight,
  Zap,
  Target,
  CreditCard,
  LineChart,
  Gift,
  Percent,
  Upload,
  Layout,
  Plus,
  X,
  Check,
  CheckCircle,
  AlertCircle,
  Save,
  Trash2,
  Settings,
  Image,
  Calendar as CalendarIcon,
  DollarSign as DollarIcon,
  Users as UsersIcon,
  MapPin as MapPinIcon,
  Info,
  Building2,
  ToggleLeft,
  ToggleRight,
  Search,
  Mail,
  Phone
} from 'lucide-react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useMasterAuth } from '../hooks/useMasterAuth';
import { 
  AdminDashboardStats, 
  EventRegistration, 
  AdminEvent,
  RealTimeMetrics,
  TopEvent,
  CustomerInsight,
  FinancialMetrics,
  EventPerformance,
  RealtimeUpdate,
  DashboardNotification,
  CustomerCheckoutData
} from '../types/admin';
import { customerService } from '../services/customerService';
import { exportFilteredCustomerData } from '../utils/exportUtils';
import { eventsService, Event as ServiceEvent } from '../services/eventsService';
import { adminAccountsApi } from '../services/api/adminAccountsApi';
import { Card, Button, LoadingSpinner, EmptyState } from '../components/ui';

// Use the Event type from the service
type Event = ServiceEvent;

interface PromoCode {
  id: string;
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  description: string;
  minAmount: number;
  maxDiscount?: number;
  validUntil: string;
  usageLimit: number;
  usedCount: number;
  isActive: boolean;
  createdAt: string;
}

interface VenueLayout {
  id: string;
  name: string;
  capacity: number;
  layout: string;
  svgContent?: string;
  sections: {
    id: string;
    name: string;
    price: number;
    seats: number;
  }[];
  createdAt: string;
  updatedAt: string;
}

const AdminDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, isClient, loading: authLoading } = useAdminAuth();
  const { isMaster } = useMasterAuth();
  const { getCurrentAdmin, adminLogout, isAuthenticated, user } = useAuth();
  
  // Get current admin account
  const [currentAdmin, setCurrentAdmin] = useState(getCurrentAdmin());
  
  // Master company selection
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>('');
  const [adminAccounts, setAdminAccounts] = useState<any[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  
  // State management
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [realTimeMetrics, setRealTimeMetrics] = useState<RealTimeMetrics | null>(null);
  const [topEvents, setTopEvents] = useState<TopEvent[]>([]);
  const [customerInsights, setCustomerInsights] = useState<CustomerInsight | null>(null);
  const [financialMetrics, setFinancialMetrics] = useState<FinancialMetrics | null>(null);
  const [eventPerformance, setEventPerformance] = useState<EventPerformance[]>([]);
  const [realtimeUpdates, setRealtimeUpdates] = useState<RealtimeUpdate[]>([]);
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('7d');
  const [activeTab, setActiveTab] = useState<'overview' | 'events' | 'analytics' | 'customers' | 'financial' | 'marketing' | 'venues'>('overview');
  const [loadingData, setLoadingData] = useState(true);
  
  // Customer data states
  const [customerData, setCustomerData] = useState<CustomerCheckoutData[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerCheckoutData[]>([]);
  const [customerFilter, setCustomerFilter] = useState<'all' | 'info_filled' | 'order_placed' | 'payment_completed'>('all');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  
  // Modal states
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [editingVenue, setEditingVenue] = useState<VenueLayout | null>(null);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  
  // Data states
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([
    {
      id: '1',
      code: 'WELCOME10',
      type: 'percentage',
      value: 10,
      description: '10% off for new users',
      minAmount: 50,
      validUntil: '2025-12-31',
      usageLimit: 1000,
      usedCount: 150,
      isActive: true,
      createdAt: '2024-01-01'
    },
    {
      id: '2',
      code: 'EARLY20',
      type: 'percentage',
      value: 20,
      description: '20% early bird discount',
      minAmount: 100,
      maxDiscount: 50,
      validUntil: '2025-06-30',
      usageLimit: 500,
      usedCount: 250,
      isActive: true,
      createdAt: '2024-01-15'
    },
    {
      id: '3',
      code: 'SAVE25',
      type: 'fixed',
      value: 25,
      description: '$25 off your order',
      minAmount: 150,
      validUntil: '2025-03-31',
      usageLimit: 200,
      usedCount: 45,
      isActive: false,
      createdAt: '2024-02-01'
    }
  ]);
  
  const [venueLayouts, setVenueLayouts] = useState<VenueLayout[]>([
    {
      id: '1',
      name: 'Amman Arena',
      capacity: 5000,
      layout: 'arena.svg',
      sections: [
        { id: '1', name: 'VIP', price: 200, seats: 100 },
        { id: '2', name: 'Premium', price: 150, seats: 500 },
        { id: '3', name: 'General', price: 100, seats: 4400 }
      ],
      createdAt: '2024-01-01',
      updatedAt: '2024-01-01'
    },
    {
      id: '2',
      name: 'Royal Cultural Center',
      capacity: 1200,
      layout: 'theater.svg',
      sections: [
        { id: '1', name: 'Orchestra', price: 120, seats: 300 },
        { id: '2', name: 'Balcony', price: 80, seats: 900 }
      ],
      createdAt: '2024-01-10',
      updatedAt: '2024-01-10'
    }
  ]);

  // Form states
  const [newPromo, setNewPromo] = useState({
    code: '',
    type: 'percentage' as 'percentage' | 'fixed',
    value: 0,
    description: '',
    minAmount: 0,
    maxDiscount: 0,
    validUntil: '',
    usageLimit: 0
  });

  const [newVenue, setNewVenue] = useState({
    name: '',
    capacity: 0,
    sections: [{ name: '', price: 0, seats: 0, benefits: [] }]
  });

  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    venue: '',
    venueId: '',
    category: 'Music',
    price: 0,
    capacity: 0,
    image: '',
    tags: [] as string[]
  });

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    // Only load data if user is authenticated and admin check is complete
    if (isAuthenticated && user && !authLoading) {
      if (isMaster) {
        // For master users, load accounts first (only once)
        if (!accountsLoaded && adminAccounts.length === 0 && !loadingAccounts) {
          loadAdminAccounts();
        }
      } else if (isAdmin || isClient) {
        // For regular admins, set their company name from currentAdmin and load data
        if (currentAdmin && currentAdmin.companyName) {
          setSelectedCompanyName(currentAdmin.companyName);
        }
        // Stats will be calculated when data loads
        loadCustomerData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, authLoading, isAdmin, isClient, isMaster, accountsLoaded]);

  // Load admin accounts for master selection
  const loadAdminAccounts = async () => {
    if (accountsLoaded) return; // Prevent multiple calls
    setLoadingAccounts(true);
    try {
      const response = await adminAccountsApi.getAll();
      const accounts = response.data || [];
      setAdminAccounts(accounts);
      setAccountsLoaded(true);
      
      // Get unique company names
      const uniqueCompanies = Array.from(new Set(accounts.map((acc: any) => acc.companyName)));
      
      // If no company selected yet and there are companies, select the first one
      if (!selectedCompanyName && uniqueCompanies.length > 0) {
        setSelectedCompanyName(uniqueCompanies[0] as string);
      }
    } catch (error) {
      console.error('Error loading admin accounts:', error);
      setAccountsLoaded(true); // Mark as loaded even on error to prevent infinite retries
    } finally {
      setLoadingAccounts(false);
    }
  };

  // When company is selected, load data for that company
  useEffect(() => {
    if (isMaster && selectedCompanyName && adminAccounts.length > 0 && accountsLoaded) {
      // Find the first admin account with this company name to get logo and details
      const companyAdmin = adminAccounts.find((acc: any) => acc.companyName === selectedCompanyName);
      if (companyAdmin) {
        setCurrentAdmin(companyAdmin as any);
      } else {
        // Create a mock admin object with the selected company name
        setCurrentAdmin((prev: any) => ({
          ...prev,
          companyName: selectedCompanyName
        } as any));
      }
      loadEvents();
      loadCustomerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyName, isMaster, accountsLoaded]);

  useEffect(() => {
    filterCustomers();
  }, [customerData, customerFilter, customerSearchQuery]);

  // Calculate real stats whenever events or customer data changes
  useEffect(() => {
    if (currentAdmin?.companyName && (events.length > 0 || customerData.length > 0)) {
      calculateRealStats();
    }
  }, [events, customerData, currentAdmin]);

  // Calculate real stats from actual data filtered by company
  const calculateRealStats = () => {
    if (!currentAdmin?.companyName) {
      setStats({
        totalRevenue: 0,
        totalEvents: 0,
        totalCustomers: 0,
        averageRating: 0,
        revenueGrowth: 0,
        eventsGrowth: 0,
        customersGrowth: 0,
        ratingGrowth: 0
      });
      return;
    }

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    // Calculate total revenue from customer data
    const totalRevenue = customerData.reduce((sum, customer) => {
      const orderData = (customer as any).orderData;
      if (orderData && orderData.total) {
        return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
      }
      return sum;
    }, 0);

    // Calculate monthly revenue
    const monthlyRevenue = customerData
      .filter(customer => {
        const submittedAt = new Date((customer as any).submittedAt || (customer as any).createdAt || '');
        return submittedAt >= startOfMonth;
      })
      .reduce((sum, customer) => {
        const orderData = (customer as any).orderData;
        if (orderData && orderData.total) {
          return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
        }
        return sum;
      }, 0);

    // Calculate last month revenue
    const lastMonthRevenue = customerData
      .filter(customer => {
        const submittedAt = new Date((customer as any).submittedAt || (customer as any).createdAt || '');
        return submittedAt >= lastMonth && submittedAt < startOfMonth;
      })
      .reduce((sum, customer) => {
        const orderData = (customer as any).orderData;
        if (orderData && orderData.total) {
          return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
        }
        return sum;
      }, 0);

    // Calculate revenue growth
    const revenueGrowth = lastMonthRevenue > 0 
      ? ((monthlyRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 
      : 0;

    // Calculate events stats
    const totalEvents = events.length;
    const eventsThisMonth = events.filter(evt => {
      const createdAt = new Date(evt.createdAt || evt.date || '');
      return createdAt >= startOfMonth;
    }).length;
    const eventsLastMonth = events.filter(evt => {
      const createdAt = new Date(evt.createdAt || evt.date || '');
      return createdAt >= lastMonth && createdAt < startOfMonth;
    }).length;
    const eventsGrowth = eventsLastMonth > 0 
      ? ((eventsThisMonth - eventsLastMonth) / eventsLastMonth) * 100 
      : 0;

    // Calculate customers stats
    const totalCustomers = customerData.length;
    const customersThisMonth = customerData.filter(customer => {
      const submittedAt = new Date((customer as any).submittedAt || (customer as any).createdAt || '');
      return submittedAt >= startOfMonth;
    }).length;
    const customersLastMonth = customerData.filter(customer => {
      const submittedAt = new Date((customer as any).submittedAt || (customer as any).createdAt || '');
      return submittedAt >= lastMonth && submittedAt < startOfMonth;
    }).length;
    const customersGrowth = customersLastMonth > 0 
      ? ((customersThisMonth - customersLastMonth) / customersLastMonth) * 100 
      : 0;

    // Calculate average rating (if available, otherwise use a default)
    const averageRating = 4.5; // Can be calculated from reviews if available
    const ratingGrowth = 0; // Can be calculated from reviews if available

    setStats({
      totalRevenue,
      totalEvents,
      totalCustomers,
      averageRating,
      revenueGrowth: Math.round(revenueGrowth * 10) / 10,
      eventsGrowth: Math.round(eventsGrowth * 10) / 10,
      customersGrowth: Math.round(customersGrowth * 10) / 10,
      ratingGrowth
    });

    setLoadingData(false);
  };

  const loadEvents = async () => {
    try {
      setLoadingData(true);
      const companyName = currentAdmin?.companyName;
      const allEvents = await eventsService.getAllEvents(undefined, companyName);
      setEvents(allEvents || []);
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]); // Set empty array on error
    } finally {
      setLoadingData(false);
    }
  };

  const loadCustomerData = async () => {
    try {
      const companyName = currentAdmin?.companyName;
      const allCustomerData = await customerService.getAllCustomerData(undefined, companyName);
      setCustomerData(allCustomerData || []);
    } catch (error) {
      console.error('Error loading customer data:', error);
      setCustomerData([]); // Set empty array on error
    }
  };

  const filterCustomers = () => {
    let filtered = [...customerData];

    // Filter by status
    if (customerFilter !== 'all') {
      filtered = filtered.filter(customer => customer.status === customerFilter);
    }

    // Filter by search query
    if (customerSearchQuery.trim()) {
      const query = customerSearchQuery.toLowerCase();
      filtered = filtered.filter(customer => 
        customer.firstName.toLowerCase().includes(query) ||
        customer.lastName.toLowerCase().includes(query) ||
        customer.email.toLowerCase().includes(query) ||
        customer.phone.includes(query) ||
        customer.eventTitle.toLowerCase().includes(query)
      );
    }

    // Sort by most recent first
    filtered.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());

    setFilteredCustomers(filtered);
  };

  const deleteCustomerData = (customerId: string) => {
    if (window.confirm('Are you sure you want to delete this attendee data?')) {
      customerService.deleteCustomerData(customerId);
      loadCustomerData(); // Reload data
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: CustomerCheckoutData['status']) => {
    switch (status) {
      case 'info_filled': return 'text-yellow-400 bg-yellow-400/20';
      case 'order_placed': return 'text-blue-400 bg-blue-400/20';
      case 'payment_completed': return 'text-green-400 bg-green-400/20';
      case 'cancelled': return 'text-red-400 bg-red-400/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  const getStatusLabel = (status: CustomerCheckoutData['status']) => {
    switch (status) {
      case 'info_filled': return 'Info Filled';
      case 'order_placed': return 'Order Placed';
      case 'payment_completed': return 'Payment Completed';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  };

  const handleExportCustomerData = () => {
    try {
      const result = exportFilteredCustomerData(
        customerData,
        filteredCustomers,
        {
          status: customerFilter,
          searchQuery: customerSearchQuery
        }
      );
      
      // Show success message (you could implement a toast notification here)
      alert(`✅ Successfully exported ${result.recordCount} attendee records to ${result.filename}`);
    } catch (error) {
      console.error('Error exporting customer data:', error);
      alert('❌ Failed to export attendee data. Please try again.');
    }
  };

  // Event Management Functions
  // Note: Event creation is disabled - only Master Dashboard can create events
  const handleCreateEvent = () => {
    alert('Event creation is restricted. Please contact the platform administrator to create new events.');
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setNewEvent({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time,
      venue: event.venue,
      venueId: event.venueId,
      category: event.category,
      price: event.price,
      capacity: event.capacity,
      image: event.image,
      tags: event.tags
    });
    setShowEventModal(true);
  };

  const handleUpdateEvent = () => {
    if (!editingEvent) return;

    try {
      eventsService.updateEvent(editingEvent.id, newEvent);
      loadEvents(); // Reload events to reflect changes
      
      setEditingEvent(null);
      setShowEventModal(false);
      setNewEvent({
        title: '',
        description: '',
        date: '',
        time: '',
        venue: '',
        venueId: '',
        category: 'Music',
        price: 0,
        capacity: 0,
        image: '',
        tags: []
      });
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };

  const handleDeleteEvent = (eventId: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        eventsService.deleteEvent(eventId);
        loadEvents(); // Reload events to reflect changes
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  const handlePublishEvent = (eventId: string) => {
    try {
      eventsService.publishEvent(eventId);
      loadEvents(); // Reload events to reflect changes
    } catch (error) {
      console.error('Error publishing event:', error);
    }
  };

  // Promo Code Management Functions
  const handleCreatePromo = () => {
    if (!newPromo.code || !newPromo.validUntil) return;

    const promo: PromoCode = {
      id: Date.now().toString(),
      ...newPromo,
      usedCount: 0,
      isActive: true,
      createdAt: new Date().toISOString().split('T')[0]
    };

    setPromoCodes(prev => [...prev, promo]);
    setNewPromo({
      code: '',
      type: 'percentage',
      value: 0,
      description: '',
      minAmount: 0,
      maxDiscount: 0,
      validUntil: '',
      usageLimit: 0
    });
    setShowPromoModal(false);
  };

  const handleEditPromo = (promo: PromoCode) => {
    setEditingPromo(promo);
    setNewPromo({
      code: promo.code,
      type: promo.type,
      value: promo.value,
      description: promo.description,
      minAmount: promo.minAmount,
      maxDiscount: promo.maxDiscount || 0,
      validUntil: promo.validUntil,
      usageLimit: promo.usageLimit
    });
    setShowPromoModal(true);
  };

  const handleUpdatePromo = () => {
    if (!editingPromo) return;

    setPromoCodes(prev => prev.map(promo => 
      promo.id === editingPromo.id 
        ? { ...promo, ...newPromo }
        : promo
    ));
    
    setEditingPromo(null);
    setShowPromoModal(false);
    setNewPromo({
      code: '',
      type: 'percentage',
      value: 0,
      description: '',
      minAmount: 0,
      maxDiscount: 0,
      validUntil: '',
      usageLimit: 0
    });
  };

  const handleDeletePromo = (promoId: string) => {
    if (window.confirm('Are you sure you want to delete this promo code?')) {
      setPromoCodes(prev => prev.filter(promo => promo.id !== promoId));
    }
  };

  const handleTogglePromo = (promoId: string) => {
    setPromoCodes(prev => prev.map(promo => 
      promo.id === promoId 
        ? { ...promo, isActive: !promo.isActive }
        : promo
    ));
  };

  // Venue Management Functions
  // Note: Venue creation is disabled - only Master Dashboard can create venues
  const handleCreateVenue = () => {
    alert('Venue creation is restricted. Please contact the platform administrator to add new venues.');
    return;
    if (!newVenue.name || !newVenue.capacity) return;

    const venue: VenueLayout = {
      id: Date.now().toString(),
      name: newVenue.name,
      capacity: newVenue.capacity,
      layout: `${newVenue.name.toLowerCase().replace(/\s+/g, '-')}.svg`,
      sections: newVenue.sections.map((section, index) => ({
        id: (index + 1).toString(),
        name: section.name,
        price: section.price,
        seats: section.seats,
        benefits: section.benefits || []
      })),
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0]
    };

    setVenueLayouts(prev => [...prev, venue]);
    setNewVenue({
      name: '',
      capacity: 0,
      sections: [{ name: '', price: 0, seats: 0, benefits: [] }]
    });
    setShowVenueModal(false);
  };

  const handleEditVenue = (venue: VenueLayout) => {
    setEditingVenue(venue);
    setNewVenue({
      name: venue.name,
      capacity: venue.capacity,
      sections: venue.sections.map(section => ({
        name: section.name,
        price: section.price,
        seats: section.seats,
        benefits: (section as any).benefits || []
      }))
    });
    setShowVenueModal(true);
  };

  const handleUpdateVenue = () => {
    if (!editingVenue) return;

    setVenueLayouts(prev => prev.map(venue => 
      venue.id === editingVenue.id 
        ? { 
            ...venue, 
            name: newVenue.name,
            capacity: newVenue.capacity,
            sections: newVenue.sections.map((section, index) => ({
              id: (index + 1).toString(),
              name: section.name,
              price: section.price,
              seats: section.seats
            })),
            updatedAt: new Date().toISOString().split('T')[0]
          }
        : venue
    ));
    
    setEditingVenue(null);
    setShowVenueModal(false);
    setNewVenue({
      name: '',
      capacity: 0,
      sections: [{ name: '', price: 0, seats: 0, benefits: [] }]
    });
  };

  const handleDeleteVenue = (venueId: string) => {
    if (window.confirm('Are you sure you want to delete this venue?')) {
      setVenueLayouts(prev => prev.filter(venue => venue.id !== venueId));
    }
  };

  const addVenueSection = () => {
    setNewVenue(prev => ({
      ...prev,
      sections: [...prev.sections, { name: '', price: 0, seats: 0, benefits: [] }]
    }));
  };

  const removeVenueSection = (index: number) => {
    setNewVenue(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const updateVenueSection = (index: number, field: string, value: string | number) => {
    setNewVenue(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  // File upload handler
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'image/svg+xml') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const svgContent = e.target?.result as string;
        // In a real app, you'd upload this to your server
        console.log('SVG uploaded:', svgContent);
      };
      reader.readAsText(file);
    }
  };

  // Show loading state while checking authentication
  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen theme-transition flex items-center justify-center" style={{background: 'var(--liquid-gradient-bg)'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-lg" style={{color: 'var(--liquid-text-primary)'}}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    navigate('/admin-login');
    return null;
  }

  // Show access denied only if loading is complete and user is not admin/client/master
  if (!authLoading && !isAdmin && !isClient && !isMaster) {
    return (
      <div className="min-h-screen theme-transition flex items-center justify-center" style={{background: 'var(--liquid-gradient-bg)'}}>
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Access Denied</h1>
          <p className="mb-4" style={{color: 'var(--liquid-text-primary)'}}>You don't have permission to access this page.</p>
          <Button
            onClick={() => navigate('/admin-login')}
            variant="glass"
          >
            Go to Admin Login
          </Button>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{background: 'var(--liquid-gradient-bg)'}}>
      {/* Enhanced Liquid Glass Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-20 right-1/4 w-48 h-48 liquid-glass opacity-8 liquid-animate-pulse" style={{animationDelay: '6s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-72 h-72 liquid-glass opacity-12 liquid-animate-morph" style={{animationDelay: '8s'}}></div>
      </div>

      <div className="relative z-10 p-6 pt-24 pb-20">
        {/* Company Selector for Master Users ONLY - Regular admin accounts will NOT see this */}
        {isMaster && !isAdmin && !isClient && (
          <Card variant="glass" className="p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>
                  Select Company to View Dashboard:
                </label>
                {loadingAccounts ? (
                  <div className="text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Loading companies...</div>
                ) : (
                  <select
                    value={selectedCompanyName}
                    onChange={(e) => setSelectedCompanyName(e.target.value)}
                    className="w-full max-w-md px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="" className="bg-white text-gray-900">Select a company...</option>
                    {Array.from(new Set(adminAccounts.map((acc: any) => acc.companyName))).map((companyName) => (
                      <option key={companyName as string} value={companyName as string} className="bg-white text-gray-900">
                        {companyName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {selectedCompanyName && (
                <div className="text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                  Viewing: <span className="font-semibold">{selectedCompanyName}</span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Show message if master user hasn't selected a company - Only for master users */}
        {isMaster && !isAdmin && !isClient && !selectedCompanyName && !loadingAccounts && (
          <Card variant="glass" className="p-8 text-center">
            <Building2 size={48} className="mx-auto mb-4 opacity-50" style={{color: 'var(--liquid-text-primary)'}} />
            <h2 className="text-xl font-bold mb-2" style={{color: 'var(--liquid-text-primary)'}}>Select a Company</h2>
            <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
              Please select a company from the dropdown above to view its admin dashboard.
            </p>
          </Card>
        )}

        {/* Dashboard Content - Show for regular admins OR for master users who have selected a company */}
        {((!isMaster && (isAdmin || isClient)) || (isMaster && selectedCompanyName)) && (
          <>
            {/* Enhanced Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                {currentAdmin?.logo ? (
                  <img
                    src={currentAdmin.logo}
                    alt={currentAdmin.companyName}
                    className="w-16 h-16 rounded-2xl object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center">
                    <Building2 size={32} className="" style={{color: 'var(--liquid-text-primary)'}} />
                  </div>
                )}
                <div>
                  <h1 className="text-3xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                    {currentAdmin?.companyName || 'Admin Dashboard'}
                  </h1>
              <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                {currentAdmin?.description || 'Manage your events, promotions, and venue layouts'}
              </p>
              {currentAdmin && (
                <div className="flex items-center space-x-4 mt-2">
                  <span className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>{currentAdmin.email}</span>
                  <span className="text-xs px-2 py-1 bg-primary-500/20 text-primary-300 rounded-lg">
                    {((currentAdmin as any).subscription?.plan || (currentAdmin as any).plan || 'BASIC').toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
          
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 glass-strong rounded-lg p-1">
          {[
            { key: 'overview', label: 'Overview', icon: BarChart3 },
            { key: 'events', label: 'Events', icon: Calendar },
            { key: 'analytics', label: 'Analytics', icon: TrendingUp },
            { key: 'customers', label: 'Customers', icon: Users },
            { key: 'financial', label: 'Financial', icon: DollarSign },
            { key: 'marketing', label: 'Marketing', icon: Gift },
            { key: 'venues', label: 'Venues', icon: Layout }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                  activeTab === tab.key 
                    ? 'bg-white/20 glow' 
                    : 'opacity-70 hover:opacity-100 hover:bg-white/10'
                }`}
                style={{color: 'var(--liquid-text-primary)'}}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

      <div className="px-6 pb-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Revenue</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{formatCurrency(stats?.totalRevenue || 0)}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-2xl flex items-center justify-center">
                    <DollarSign size={24} className="text-green-400" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-green-400 text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  +{stats?.revenueGrowth || 0}% from last month
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Events</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{stats?.totalEvents || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                    <Calendar size={24} className="text-blue-400" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-green-400 text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  +{stats?.eventsGrowth || 0}% from last month
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Attendees</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{stats?.totalCustomers || 0}</p>
                  </div>
                  <div className="w-12 h-12 style={{backgroundColor: '#f9bb1020'}} rounded-2xl flex items-center justify-center">
                    <Users size={24} className="style={{color: '#f9bb10'}}" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-green-400 text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  +{stats?.customersGrowth || 0}% from last month
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Average Rating</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{stats?.averageRating || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-2xl flex items-center justify-center">
                    <Star size={24} className="text-yellow-400" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-green-400 text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  +{stats?.ratingGrowth || 0}% from last month
                </div>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card variant="glass" className="p-6">
              <h2 className="text-xl font-bold  mb-6" style={{color: 'var(--liquid-text-primary)'}}>Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button
                  onClick={() => setShowPromoModal(true)}
                  variant="glass"
                  className="p-6 rounded-2xl text-left justify-start h-auto"
                >
                  <div>
                    <Gift size={24} className="text-green-400 mb-3" />
                    <h3 className="font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>Create Promo</h3>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Set up promotional campaigns</p>
                  </div>
                </Button>

                <div className="glass p-6 rounded-2xl text-left opacity-60">
                  <Info size={24} className="text-blue-400 mb-3" />
                  <h3 className="font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>Need to Create Events or Venues?</h3>
                  <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Contact the platform administrator to create new events or add venues.</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Event Management</h2>
                <p className="opacity-70 text-sm mt-1" style={{color: 'var(--liquid-text-primary)'}}>
                  View and manage your events. Contact platform administrator to create new events.
                </p>
              </div>
            </div>

            {/* Events List */}
            <Card variant="glass" className="p-6">
              <div className="space-y-4">
                {events.map((event) => (
                  <div key={event.id} className="glass p-6 rounded-2xl">
                    <div className="flex items-start space-x-4">
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-20 h-20 rounded-xl object-cover"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold  text-lg" style={{color: 'var(--liquid-text-primary)'}}>{event.title}</h3>
                            <p className="opacity-70 text-sm mb-2" style={{color: 'var(--liquid-text-primary)'}}>{event.description}</p>
                            <div className="flex items-center space-x-4 text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>
                              <span className="flex items-center">
                                <CalendarIcon size={14} className="mr-1" />
                                {event.date}
                              </span>
                              <span className="flex items-center">
                                <Clock size={14} className="mr-1" />
                                {event.time}
                              </span>
                              <span className="flex items-center">
                                <MapPinIcon size={14} className="mr-1" />
                                {event.venue}
                              </span>
                              <span className="flex items-center">
                                <DollarIcon size={14} className="mr-1" />
                                ${event.price}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              event.status === 'published' ? 'bg-green-500/20 text-green-400' :
                              event.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                              event.status === 'sold_out' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {event.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center space-x-4 text-sm">
                            <span className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                              Sold: {event.ticketsSold}/{event.capacity}
                            </span>
                            <div className="w-32 bg-white/20 rounded-full h-2">
                              <div 
                                className="bg-blue-400 h-2 rounded-full"
                                style={{ width: `${(event.ticketsSold / event.capacity) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            {event.status === 'draft' && (
                              <button
                                onClick={() => handlePublishEvent(event.id)}
                                className="px-4 py-2 bg-green-500/20 text-green-400 rounded-xl text-sm font-medium hover:scale-105 transition-transform"
                              >
                                Publish
                              </button>
                            )}
                            <Button
                              onClick={() => handleEditEvent(event)}
                              variant="glass"
                              size="icon"
                              className="p-2 rounded-xl"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              onClick={() => handleDeleteEvent(event.id)}
                              variant="glass"
                              size="icon"
                              className="p-2 rounded-xl"
                            >
                              <Trash2 size={16} className="text-red-400" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Marketing Tab - Promo Codes Management */}
        {activeTab === 'marketing' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Marketing & Promotions</h2>
              <Button
                onClick={() => setShowPromoModal(true)}
                variant="glass"
                size="lg"
              >
                <Plus size={20} className="mr-2" />
                Create Promo Code
              </Button>
            </div>

            {/* Promo Codes List */}
            <Card variant="glass" className="p-6">
              <h3 className="text-xl font-bold  mb-6" style={{color: 'var(--liquid-text-primary)'}}>Promo Codes</h3>
              <div className="space-y-4">
                {promoCodes.map((promo) => (
                  <div key={promo.id} className="glass p-4 rounded-2xl flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        promo.isActive ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        <Percent size={20} className={promo.isActive ? 'text-green-400' : 'text-red-400'} />
                      </div>
                      <div>
                        <h4 className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>{promo.code}</h4>
                        <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>{promo.description}</p>
                        <div className="flex items-center space-x-4 text-xs opacity-60 mt-1" style={{color: 'var(--liquid-text-primary)'}}>
                          <span>{promo.type === 'percentage' ? `${promo.value}% off` : `$${promo.value} off`}</span>
                          <span>Min: ${promo.minAmount}</span>
                          <span>Expires: {promo.validUntil}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6">
                      <div className="text-center">
                        <div className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>{promo.usedCount}/{promo.usageLimit}</div>
                        <div className="opacity-60 text-xs" style={{color: 'var(--liquid-text-primary)'}}>Usage</div>
                      </div>
                      <button
                        onClick={() => handleTogglePromo(promo.id)}
                        className="flex items-center space-x-2"
                      >
                        {promo.isActive ? (
                          <ToggleRight size={24} className="text-green-400" />
                        ) : (
                          <ToggleLeft size={24} className="text-gray-400" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditPromo(promo)}
                        className="opacity-60 hover:text-white" style={{color: 'var(--liquid-text-primary)'}}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeletePromo(promo.id)}
                        className="text-red-400opacity-60 hover:text-red-400"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Venues Tab - Venue Layout Management */}
        {activeTab === 'venues' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Venue Management</h2>
                <p className="opacity-70 text-sm mt-1" style={{color: 'var(--liquid-text-primary)'}}>
                  View and manage your venues. Contact platform administrator to add new venues.
                </p>
              </div>
            </div>

            {/* Venue Layouts List */}
            <Card variant="glass" className="p-6">
              <h3 className="text-xl font-bold  mb-6" style={{color: 'var(--liquid-text-primary)'}}>Venue Layouts</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {venueLayouts.map((venue) => (
                  <div key={venue.id} className="glass p-6 rounded-2xl">
                    <div className="w-full h-32 bg-white/10 rounded-xl mb-4 flex items-center justify-center">
                      <Layout size={48} className="opacity-40" style={{color: 'var(--liquid-text-primary)'}} />
                    </div>
                    <h4 className="font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>{venue.name}</h4>
                    <p className="opacity-70 text-sm mb-2" style={{color: 'var(--liquid-text-primary)'}}>Capacity: {venue.capacity.toLocaleString()}</p>
                    <div className="opacity-60 text-xs mb-4" style={{color: 'var(--liquid-text-primary)'}}>
                      {venue.sections.length} sections • Updated {venue.updatedAt}
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditVenue(venue)}
                        className="px-4 py-2 rounded-xl  text-sm hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                      >
                        <Edit size={14} className="mr-1" />
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteVenue(venue.id)}
                        className="px-4 py-2 rounded-xl text-red-400 text-sm hover:scale-105 transition-transform"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Analytics Dashboard</h2>
            <Card variant="glass" className="p-6">
              <div className="text-center py-12">
                <LineChart size={64} className="mx-auto opacity-40 mb-4" style={{color: 'var(--liquid-text-primary)'}} />
                <h3 className="text-xl font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>Analytics Coming Soon</h3>
                <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Detailed analytics and reporting features will be available here.</p>
              </div>
            </Card>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Attendees Management</h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleExportCustomerData}
                  disabled={filteredCustomers.length === 0}
                  className="px-4 py-2 rounded-xl  font-medium hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed" style={{color: 'var(--liquid-text-primary)'}}
                  title="Export attendees data to Excel"
                >
                  <Download size={16} className="mr-2" />
                  Export Data
                </button>
                <button
                  onClick={loadCustomerData}
                  className="glass-button px-4 py-2 rounded-xl  font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <RefreshCw size={16} className="mr-2" />
                  Refresh
                </button>
              </div>
            </div>

            {/* Customer Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Attendees</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{customerData.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                    <Users size={24} className="text-blue-400" />
                  </div>
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Completed Orders</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {customerData.filter(c => c.isCompleted).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-2xl flex items-center justify-center">
                    <CheckCircle size={24} className="text-green-400" />
                  </div>
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Info Only</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {customerData.filter(c => c.status === 'info_filled').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-2xl flex items-center justify-center">
                    <AlertCircle size={24} className="text-yellow-400" />
                  </div>
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Conversion Rate</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {customerData.length > 0 
                        ? Math.round((customerData.filter(c => c.isCompleted).length / customerData.length) * 100)
                        : 0}%
                    </p>
                  </div>
                  <div className="w-12 h-12 style={{backgroundColor: '#f9bb1020'}} rounded-2xl flex items-center justify-center">
                    <TrendingUp size={24} className="style={{color: '#f9bb10'}}" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Filters and Search */}
            <Card variant="glass" className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search attendees..."
                      value={customerSearchQuery}
                      onChange={(e) => setCustomerSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200 w-64"
                    />
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 opacity-60" style={{color: 'var(--liquid-text-primary)'}} />
                  </div>
                  
                  <select
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value as typeof customerFilter)}
                    className="px-4 py-2 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="all" className="bg-white text-gray-900">All Status</option>
                    <option value="info_filled" className="bg-white text-gray-900">Info Filled</option>
                    <option value="order_placed" className="bg-white text-gray-900">Order Placed</option>
                    <option value="payment_completed" className="bg-white text-gray-900">Payment Completed</option>
                  </select>
                </div>

                <div className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>
                  Showing {filteredCustomers.length} of {customerData.length} customers
                  {filteredCustomers.length > 0 && (
                    <div className="text-xs mt-1">
                      Export will include {filteredCustomers.length} filtered records
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Attendees List */}
            <Card variant="glass" className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Attendees Data</h3>
                {filteredCustomers.length > 0 && (
                  <span className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>
                    💡 Use "Export Data" to download as Excel spreadsheet
                  </span>
                )}
              </div>
              
              {filteredCustomers.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={64} className="mx-auto opacity-40 mb-4" style={{color: 'var(--liquid-text-primary)'}} />
                  <h3 className="text-xl font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>No Attendees Data</h3>
                  <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Customer checkout information will appear here when users fill out forms.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredCustomers.map((customer) => (
                    <div key={customer.id} className="glass p-6 rounded-2xl">
                      {/* Header row: name + status + delete */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-base" style={{color: 'var(--liquid-text-primary)'}}>
                            {customer.firstName} {customer.lastName}
                          </h4>
                          <div className="text-xs opacity-60 mt-0.5" style={{color: 'var(--liquid-text-primary)'}}>
                            Submitted: {formatDateTime(customer.submittedAt)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(customer.status)}`}>
                            {getStatusLabel(customer.status)}
                          </span>
                          <button
                            onClick={() => deleteCustomerData(customer.id)}
                            className="p-2 glass-button rounded-xl hover:scale-105 transition-transform"
                            title="Delete attendee data"
                          >
                            <Trash2 size={16} className="text-red-400" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        {/* Customer Contact */}
                        <div>
                          <h5 className="font-medium text-xs uppercase tracking-wide opacity-50 mb-2" style={{color: 'var(--liquid-text-primary)'}}>Contact</h5>
                          <div className="space-y-1 text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                            <div className="flex items-center">
                              <Mail size={13} className="mr-2 flex-shrink-0" />
                              <span className="truncate">{customer.email}</span>
                            </div>
                            <div className="flex items-center">
                              <Phone size={13} className="mr-2 flex-shrink-0" />
                              {customer.phone}
                            </div>
                          </div>
                        </div>

                        {/* Event & Order Info */}
                        <div>
                          <h5 className="font-medium text-xs uppercase tracking-wide opacity-50 mb-2" style={{color: 'var(--liquid-text-primary)'}}>Order</h5>
                          <div className="space-y-1 text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                            <div className="font-medium opacity-90">{customer.eventTitle}</div>
                            <div className="flex items-center">
                              <CreditCard size={13} className="mr-2 flex-shrink-0" />
                              <span className="capitalize">{customer.paymentMethod?.replace(/([A-Z])/g, ' $1').trim()}</span>
                            </div>
                            {customer.orderData && (
                              <>
                                <div>{customer.orderData.ticketType} × {customer.orderData.quantity}</div>
                                <div className="font-semibold text-green-400">
                                  {customer.orderData.total.toFixed(2)} JOD
                                </div>
                                {customer.orderData.promoCode && (
                                  <div className="text-xs text-yellow-400">Promo: {customer.orderData.promoCode}</div>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Address Info */}
                        <div>
                          <h5 className="font-medium text-xs uppercase tracking-wide opacity-50 mb-2" style={{color: 'var(--liquid-text-primary)'}}>Address</h5>
                          <div className="space-y-1 text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                            {(customer.deliveryAddress || customer.billingAddress) ? (
                              <div className="flex items-start">
                                <MapPin size={13} className="mr-2 mt-0.5 flex-shrink-0" />
                                <div>
                                  <div>{customer.deliveryAddress || customer.billingAddress}</div>
                                  <div>{customer.deliveryCity || customer.city}{customer.country ? `, ${customer.country}` : ''}</div>
                                </div>
                              </div>
                            ) : (
                              <span className="opacity-40">—</span>
                            )}
                            {customer.deliveryNotes && (
                              <div className="text-xs bg-white/10 p-2 rounded mt-1">
                                📝 {customer.deliveryNotes}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Attendees (for multi-booking) */}
                        <div>
                          <h5 className="font-medium text-xs uppercase tracking-wide opacity-50 mb-2" style={{color: 'var(--liquid-text-primary)'}}>
                            Attendees {customer.bookingType === 'multi' ? `(${customer.attendeeNames?.length || 0})` : ''}
                          </h5>
                          <div className="space-y-1 text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                            {customer.bookingType === 'multi' && customer.attendeeNames && customer.attendeeNames.length > 0 ? (
                              <div className="space-y-1">
                                {customer.attendeeNames.map((name, idx) => (
                                  <div key={idx} className="flex items-center">
                                    <span className="w-5 h-5 text-xs rounded-full bg-white/10 flex items-center justify-center mr-2 flex-shrink-0">
                                      {idx + 1}
                                    </span>
                                    <span>{name || <span className="opacity-40">—</span>}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex items-center">
                                <span className="w-5 h-5 text-xs rounded-full bg-white/10 flex items-center justify-center mr-2 flex-shrink-0">1</span>
                                <span>{customer.firstName} {customer.lastName}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Financial Tab */}
        {activeTab === 'financial' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Financial Overview</h2>
            
            {/* Financial Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Revenue</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {formatCurrency(customerData.reduce((sum, c) => {
                        const orderData = (c as any).orderData;
                        return sum + (orderData?.total || 0);
                      }, 0))}
                    </p>
                  </div>
                  <DollarSign size={32} className="text-green-400 opacity-60" />
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>This Month</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {formatCurrency((() => {
                        const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                        return customerData
                          .filter(c => {
                            const submittedAt = new Date((c as any).submittedAt || (c as any).createdAt || '');
                            return submittedAt >= startOfMonth;
                          })
                          .reduce((sum, c) => {
                            const orderData = (c as any).orderData;
                            return sum + (orderData?.total || 0);
                          }, 0);
                      })())}
                    </p>
                  </div>
                  <Calendar size={32} className="text-blue-400 opacity-60" />
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Orders</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {customerData.filter(c => c.isCompleted).length}
                    </p>
                  </div>
                  <CreditCard size={32} className="text-purple-400 opacity-60" />
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Avg Order Value</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {formatCurrency((() => {
                        const completedOrders = customerData.filter(c => c.isCompleted);
                        if (completedOrders.length === 0) return 0;
                        const total = completedOrders.reduce((sum, c) => {
                          const orderData = (c as any).orderData;
                          return sum + (orderData?.total || 0);
                        }, 0);
                        return total / completedOrders.length;
                      })())}
                    </p>
                  </div>
                  <Target size={32} className="text-yellow-400 opacity-60" />
                </div>
              </Card>
            </div>

            {/* Revenue by Payment Method */}
            <Card variant="glass" className="p-6">
              <h3 className="text-xl font-bold mb-6" style={{color: 'var(--liquid-text-primary)'}}>Revenue by Payment Method</h3>
              <div className="space-y-4">
                {(() => {
                  const paymentMethods: { [key: string]: number } = {};
                  customerData.forEach(customer => {
                    const method = customer.paymentMethod || 'unknown';
                    const orderData = (customer as any).orderData;
                    const amount = orderData?.total || 0;
                    paymentMethods[method] = (paymentMethods[method] || 0) + amount;
                  });
                  const total = Object.values(paymentMethods).reduce((sum, val) => sum + val, 0);
                  return Object.entries(paymentMethods)
                    .sort((a, b) => b[1] - a[1])
                    .map(([method, amount]) => (
                      <div key={method} className="flex items-center justify-between p-4 glass rounded-2xl">
                        <div className="flex items-center space-x-4">
                          <CreditCard size={20} className="text-blue-400" />
                          <div>
                            <p className="font-semibold capitalize" style={{color: 'var(--liquid-text-primary)'}}>{method}</p>
                            <p className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>
                              {total > 0 ? Math.round((amount / total) * 100) : 0}% of total
                            </p>
                          </div>
                        </div>
                        <p className="font-bold text-green-400">{formatCurrency(amount)}</p>
                      </div>
                    ));
                })()}
                {customerData.length === 0 && (
                  <p className="text-center py-8 opacity-60" style={{color: 'var(--liquid-text-primary)'}}>No payment data available</p>
                )}
              </div>
            </Card>

            {/* Monthly Revenue Breakdown */}
            <Card variant="glass" className="p-6">
              <h3 className="text-xl font-bold mb-6" style={{color: 'var(--liquid-text-primary)'}}>Monthly Revenue (Last 6 Months)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {(() => {
                  const months = [];
                  for (let i = 5; i >= 0; i--) {
                    const date = new Date();
                    date.setMonth(date.getMonth() - i);
                    const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
                    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
                    const revenue = customerData
                      .filter(c => {
                        const submittedAt = new Date((c as any).submittedAt || (c as any).createdAt || '');
                        return submittedAt >= monthStart && submittedAt <= monthEnd;
                      })
                      .reduce((sum, c) => {
                        const orderData = (c as any).orderData;
                        return sum + (orderData?.total || 0);
                      }, 0);
                    months.push({ month: monthKey, revenue });
                  }
                  return months.map((m, index) => (
                    <div key={index} className="p-4 glass rounded-xl text-center">
                      <p className="text-xs opacity-70 mb-2" style={{color: 'var(--liquid-text-primary)'}}>{m.month}</p>
                      <p className="text-lg font-bold" style={{color: 'var(--liquid-text-primary)'}}>{formatCurrency(m.revenue)}</p>
                    </div>
                  ));
                })()}
              </div>
            </Card>

            {/* Event Revenue Breakdown */}
            <Card variant="glass" className="p-6">
              <h3 className="text-xl font-bold mb-6" style={{color: 'var(--liquid-text-primary)'}}>Revenue by Event</h3>
              <div className="space-y-3">
                {events
                  .map(event => {
                    const eventCustomers = customerData.filter(c => c.eventId === event.id);
                    const eventRevenue = eventCustomers.reduce((sum, c) => {
                      const orderData = (c as any).orderData;
                      return sum + (orderData?.total || 0);
                    }, 0);
                    return { ...event, revenue: eventRevenue, customers: eventCustomers.length };
                  })
                  .sort((a, b) => b.revenue - a.revenue)
                  .map(event => (
                    <div key={event.id} className="flex items-center justify-between p-4 glass rounded-2xl">
                      <div className="flex-1">
                        <h4 className="font-semibold mb-1" style={{color: 'var(--liquid-text-primary)'}}>{event.title}</h4>
                        <p className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>
                          {event.customers} customers • {new Date(event.date || '').toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-400">{formatCurrency(event.revenue)}</p>
                      </div>
                    </div>
                  ))}
                {events.length === 0 && (
                  <p className="text-center py-8 opacity-60" style={{color: 'var(--liquid-text-primary)'}}>No events data available</p>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Event Edit Modal (Create disabled - only Master Dashboard can create) */}
        {showEventModal && editingEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <Card variant="glass" className="p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                  Edit Event
                </h3>
                <button
                  onClick={() => {
                    setShowEventModal(false);
                    setEditingEvent(null);
                    setNewEvent({
                      title: '',
                      description: '',
                      date: '',
                      time: '',
                      venue: '',
                      venueId: '',
                      category: 'Music',
                      price: 0,
                      capacity: 0,
                      image: '',
                      tags: []
                    });
                  }}
                  className="opacity-60 hover:text-white" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Event Title</label>
                    <input
                      type="text"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Enter event title"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Category</label>
                    <select
                      value={newEvent.category}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                    >
                      <option value="Music" className="bg-white text-gray-900">Music</option>
                      <option value="Business" className="bg-white text-gray-900">Business</option>
                      <option value="Sports" className="bg-white text-gray-900">Sports</option>
                      <option value="Entertainment" className="bg-white text-gray-900">Entertainment</option>
                      <option value="Arts" className="bg-white text-gray-900">Arts</option>
                      <option value="Food" className="bg-white text-gray-900">Food & Drink</option>
                      <option value="Tech" className="bg-white text-gray-900">Technology</option>
                      <option value="Festival" className="bg-white text-gray-900">Festival</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200 resize-none h-24"
                    placeholder="Event description"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Date</label>
                    <input
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Time</label>
                    <input
                      type="time"
                      value={newEvent.time}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Venue</label>
                    <select
                      value={newEvent.venueId}
                      onChange={(e) => {
                        const selectedVenue = venueLayouts.find(v => v.id === e.target.value);
                        setNewEvent(prev => ({ 
                          ...prev, 
                          venueId: e.target.value,
                          venue: selectedVenue?.name || '',
                          capacity: selectedVenue?.capacity || 0
                        }));
                      }}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                      required
                    >
                      <option value="" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Select Venue</option>
                      {venueLayouts.map((venue) => (
                        <option key={venue.id} value={venue.id} className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>
                          {venue.name} (Capacity: {venue.capacity})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Ticket Price ($)</label>
                    <input
                      type="number"
                      value={newEvent.price}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, price: Number(e.target.value) }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Event Image URL</label>
                  <input
                    type="url"
                    value={newEvent.image}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, image: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
              
              <div className="flex space-x-4 mt-8">
                <button
                  onClick={() => {
                    setShowEventModal(false);
                    setEditingEvent(null);
                    setNewEvent({
                      title: '',
                      description: '',
                      date: '',
                      time: '',
                      venue: '',
                      venueId: '',
                      category: 'Music',
                      price: 0,
                      capacity: 0,
                      image: '',
                      tags: []
                    });
                  }}
                  className="flex-1 px-6 py-3 glass rounded-xl  font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateEvent}
                  className="flex-1 glass-button px-6 py-3 rounded-xl  font-medium glow hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <Save size={20} className="mr-2" />
                  Update Event
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* Promo Code Modal */}
        {showPromoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <Card variant="glass" className="p-8 max-w-md w-full">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                  {editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}
                </h3>
                <button
                  onClick={() => {
                    setShowPromoModal(false);
                    setEditingPromo(null);
                    setNewPromo({
                      code: '',
                      type: 'percentage',
                      value: 0,
                      description: '',
                      minAmount: 0,
                      maxDiscount: 0,
                      validUntil: '',
                      usageLimit: 0
                    });
                  }}
                  className="opacity-60 hover:text-white" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Promo Code</label>
                  <input
                    type="text"
                    value={newPromo.code}
                    onChange={(e) => setNewPromo(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="ENTER CODE"
                    maxLength={20}
                  />
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Type</label>
                  <select
                    value={newPromo.type}
                    onChange={(e) => setNewPromo(prev => ({ ...prev, type: e.target.value as 'percentage' | 'fixed' }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="percentage" className="bg-white text-gray-900">Percentage</option>
                    <option value="fixed" className="bg-white text-gray-900">Fixed Amount</option>
                  </select>
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>
                    {newPromo.type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                  </label>
                  <input
                    type="number"
                    value={newPromo.value}
                    onChange={(e) => setNewPromo(prev => ({ ...prev, value: Number(e.target.value) }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    min="0"
                    max={newPromo.type === 'percentage' ? 100 : undefined}
                  />
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Description</label>
                  <input
                    type="text"
                    value={newPromo.description}
                    onChange={(e) => setNewPromo(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Brief description"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Min Amount ($)</label>
                    <input
                      type="number"
                      value={newPromo.minAmount}
                      onChange={(e) => setNewPromo(prev => ({ ...prev, minAmount: Number(e.target.value) }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      min="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Usage Limit</label>
                    <input
                      type="number"
                      value={newPromo.usageLimit}
                      onChange={(e) => setNewPromo(prev => ({ ...prev, usageLimit: Number(e.target.value) }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      min="1"
                    />
                  </div>
                </div>

                {newPromo.type === 'percentage' && (
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Max Discount ($)</label>
                    <input
                      type="number"
                      value={newPromo.maxDiscount}
                      onChange={(e) => setNewPromo(prev => ({ ...prev, maxDiscount: Number(e.target.value) }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      min="0"
                      placeholder="Optional"
                    />
                  </div>
                )}
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Valid Until</label>
                  <input
                    type="date"
                    value={newPromo.validUntil}
                    onChange={(e) => setNewPromo(prev => ({ ...prev, validUntil: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  />
                </div>
              </div>
              
              <div className="flex space-x-4 mt-8">
                <button
                  onClick={() => {
                    setShowPromoModal(false);
                    setEditingPromo(null);
                    setNewPromo({
                      code: '',
                      type: 'percentage',
                      value: 0,
                      description: '',
                      minAmount: 0,
                      maxDiscount: 0,
                      validUntil: '',
                      usageLimit: 0
                    });
                  }}
                  className="flex-1 px-6 py-3 glass rounded-xl  font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  Cancel
                </button>
                <button
                  onClick={editingPromo ? handleUpdatePromo : handleCreatePromo}
                  className="flex-1 glass-button px-6 py-3 rounded-xl  font-medium glow hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <Save size={20} className="mr-2" />
                  {editingPromo ? 'Update' : 'Create'}
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* Venue Edit Modal (Create disabled - only Master Dashboard can create) */}
        {showVenueModal && editingVenue && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <Card variant="glass" className="p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                  Edit Venue Layout
                </h3>
                <button
                  onClick={() => {
                    setShowVenueModal(false);
                    setEditingVenue(null);
                    setNewVenue({
                      name: '',
                      capacity: 0,
                      sections: [{ name: '', price: 0, seats: 0, benefits: [] }]
                    });
                  }}
                  className="opacity-60 hover:text-white" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Venue Name</label>
                  <input
                    type="text"
                    value={newVenue.name}
                    onChange={(e) => setNewVenue(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Enter venue name"
                  />
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Total Capacity</label>
                  <input
                    type="number"
                    value={newVenue.capacity}
                    onChange={(e) => setNewVenue(prev => ({ ...prev, capacity: Number(e.target.value) }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Total capacity"
                    min="1"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block  font-medium" style={{color: 'var(--liquid-text-primary)'}}>Seating Sections</label>
                    <button
                      onClick={addVenueSection}
                      className="glass-button px-4 py-2 rounded-xl  text-sm hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                    >
                      <Plus size={16} className="mr-1" />
                      Add Section
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {newVenue.sections.map((section, index) => (
                      <div key={index} className="glass p-4 rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium" style={{color: 'var(--liquid-text-primary)'}}>Section {index + 1}</h4>
                          {newVenue.sections.length > 1 && (
                            <button
                              onClick={() => removeVenueSection(index)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block opacity-70 text-sm mb-1" style={{color: 'var(--liquid-text-primary)'}}>Name</label>
                            <input
                              type="text"
                              value={section.name}
                              onChange={(e) => updateVenueSection(index, 'name', e.target.value)}
                              className="w-full px-3 py-2 glass-input rounded-lg bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200 text-sm"
                              placeholder="VIP, Premium, etc."
                            />
                          </div>
                          <div>
                            <label className="block opacity-70 text-sm mb-1" style={{color: 'var(--liquid-text-primary)'}}>Price ($)</label>
                            <input
                              type="number"
                              value={section.price}
                              onChange={(e) => updateVenueSection(index, 'price', Number(e.target.value))}
                              className="w-full px-3 py-2 glass-input rounded-lg bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200 text-sm"
                              min="0"
                            />
                          </div>
                          <div>
                            <label className="block opacity-70 text-sm mb-1" style={{color: 'var(--liquid-text-primary)'}}>Seats</label>
                            <input
                              type="number"
                              value={section.seats}
                              onChange={(e) => updateVenueSection(index, 'seats', Number(e.target.value))}
                              className="w-full px-3 py-2 glass-input rounded-lg bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200 text-sm"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>SVG Layout File</label>
                  <div className="glass p-6 rounded-xl border-2 border-dashed border-white/30 text-center">
                    <Upload size={48} className="mx-auto mb-4 opacity-40" style={{color: 'var(--liquid-text-primary)'}} />
                    <p className="opacity-70 mb-2" style={{color: 'var(--liquid-text-primary)'}}>Drag & drop your SVG file here</p>
                    <p className="/50 text-sm" style={{color: 'var(--liquid-text-primary)'}}>or click to browse</p>
                    <input
                      type="file"
                      accept=".svg"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-4 mt-8">
                <button
                  onClick={() => {
                    setShowVenueModal(false);
                    setEditingVenue(null);
                    setNewVenue({
                      name: '',
                      capacity: 0,
                      sections: [{ name: '', price: 0, seats: 0, benefits: [] }]
                    });
                  }}
                  className="flex-1 px-6 py-3 glass rounded-xl  font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateVenue}
                  className="flex-1 glass-button px-6 py-3 rounded-xl  font-medium glow hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <Save size={20} className="mr-2" />
                  Update Venue
                </button>
              </div>
            </Card>
          </div>
        )}
      </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 